import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createOrder, createAutoOrders, getOrders,
  deleteOrder, getSingleOrder, updateOrder
} from '../../redux/features/orders/orderSlice';
import { getCustomers } from '../../redux/features/sales/customerSlice';
import { toast } from 'react-toastify';
import { useTable, usePagination, useGlobalFilter, useFilters } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { MdEditSquare, MdDeleteForever } from 'react-icons/md';
import { AiOutlinePlus } from "react-icons/ai";
import ButtonLoading from '../../loader/ButtonLoader';
import ReactToPrint from 'react-to-print';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import { Modal } from "bootstrap";
import Select from "react-select";
import { EthiopianDateCell } from './EthiopianDateCell';


const initialState = {
  name:        "",
  category:    "",
  quantity:    "",
  description: "",
  // Customer fields
  customer:    null,  // react-select { value, label }
  ordererName: "",
  phoneNumber: "",
};

const statusOptions = [
  { value: "pending",   label: "Pending" },
  { value: "ordered",   label: "Ordered" },
  { value: "arrived",   label: "Arrived" },
  { value: "cancelled", label: "Cancelled" },
];

const statusColors = {
  pending:   "warning",
  ordered:   "primary",
  arrived:   "success",
  cancelled: "danger",
};

const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;

export const Orders = () => {
  useRedirectLogOutUser();

  const dispatch     = useDispatch();
  const componentRef = useRef();

  const addModalRef    = useRef(null);
  const editModalRef   = useRef(null);
  const deleteModalRef = useRef(null);

  const [orderForm, setOrderForm]     = useState(initialState);
  const [editStatus, setEditStatus]   = useState(null);
  const [isLoading, setIsLoading]     = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [threshold, setThreshold]     = useState(10);
  const [printView, setPrintView]     = useState(false);
  const [editId, setEditId]           = useState(null);
  const [deleteId, setDeleteId]       = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  const { orders, order } = useSelector((state) => state.order);
  const { customers }     = useSelector((state) => state.customer);
  const { userType }      = useSelector((state) => state.auth);
  const isAdmin           = userType === "admin";

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(getOrders());
    dispatch(getCustomers());
  }, [dispatch]);

  // Populate edit form
  useEffect(() => {
    if (order && editId) {
      setOrderForm({
        name:        order.name         ?? "",
        category:    order.category     ?? "",
        quantity:    order.quantity     ?? "",
        description: order.description  ?? "",
        customer:    order.customer
          ? { value: order.customer._id, label: order.customer.name }
          : null,
        ordererName: order.orderBy?.name        ?? "",
        phoneNumber: order.orderBy?.phoneNumber ?? "",
      });
      setEditStatus(statusOptions.find(s => s.value === order.status) || statusOptions[0]);
    }
  }, [order, editId]);

  // ── Customer options ───────────────────────────────────────
  const customerOptions = useMemo(() =>
    customers.map(c => ({
      value: c._id,
      label: `${c.name}${c.phone ? ` — ${c.phone}` : ""}`
    })),
  [customers]);

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: "46px", fontSize: "1.5rem" }),
    menu:    (base) => ({ ...base, zIndex: 9999, fontSize: "1.5rem" }),
  };

  // ── Filtered orders ────────────────────────────────────────
  const filteredOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    if (filterStatus === "all") return orders;
    if (filterStatus === "auto") return orders.filter(o => o.isAutoOrder);
    return orders.filter(o => o.status === filterStatus);
  }, [orders, filterStatus]);

  // ── Modal openers ──────────────────────────────────────────
  const openAddModal = () => {
    setOrderForm(initialState);
    document.activeElement?.blur();
    new Modal(addModalRef.current).show();
  };

  const openEditModal = (id) => {
    setEditId(id);
    dispatch(getSingleOrder(id));
    document.activeElement?.blur();
    new Modal(editModalRef.current).show();
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    document.activeElement?.blur();
    new Modal(deleteModalRef.current).show();
  };

  // ── Input handler ──────────────────────────────────────────
  const handleChange = (e) => {
    setOrderForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ── Add submit — direct, modal stays open ─────────────────
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const { name, category, quantity, customer, ordererName } = orderForm;
    if (!name || !category || !quantity) {
      return toast.error("Name, category and quantity are required");
    }
    if (!customer && !ordererName) {
      return toast.error("Please select a customer or enter an orderer name");
    }

    setIsLoading(true);
    await dispatch(createOrder({
      name:        orderForm.name,
      category:    orderForm.category,
      quantity:    Number(orderForm.quantity),
      description: orderForm.description  || null,
      customerId:  customer?.value        || null,
      ordererName: ordererName            || null,
      phoneNumber: orderForm.phoneNumber  || null,
    }));
    setIsLoading(false);
    setOrderForm(initialState);
    toast.success("Order added — add another or close.");
  };

  // ── Edit submit ────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const { name, category, quantity, customer, ordererName } = orderForm;

    await dispatch(updateOrder({
      id: editId,
      data: {
        name,
        category,
        quantity:    Number(quantity),
        description: orderForm.description || null,
        customerId:  customer?.value       || null,
        ordererName: ordererName           || null,
        phoneNumber: orderForm.phoneNumber || null,
        status:      editStatus?.value     || "pending",
      }
    }));
    Modal.getInstance(editModalRef.current).hide();
  };

  // ── Auto-order ─────────────────────────────────────────────
  const handleAutoOrder = async () => {
    setAutoLoading(true);
    await dispatch(createAutoOrders(threshold));
    setAutoLoading(false);
    dispatch(getOrders());
  };

  // ── Delete ─────────────────────────────────────────────────
  const handleDelete = () => {
    dispatch(deleteOrder(deleteId));
    Modal.getInstance(deleteModalRef.current).hide();
  };

  const handlePrint = () => setPrintView(true);
  const handleBeforeGetContent = async () => await setPrintView(true);

  // ── Shared form fields ─────────────────────────────────────
  const renderFormFields = (isEdit = false) => (
    <div className="row g-3">

      {/* Item Name */}
      <div className="col-12">
        <label className="fs-4">Item Name <span className="text-danger">*</span></label>
        <input
          className="form-control form-control-lg border-2"
          type="text" name="name" placeholder="Item name"
          value={orderForm.name} onChange={handleChange}
        />
      </div>

      {/* Category + Quantity */}
      <div className="col-6">
        <label className="fs-4">Category <span className="text-danger">*</span></label>
        <input
          className="form-control form-control-lg border-2"
          type="text" name="category" placeholder="Category"
          value={orderForm.category} onChange={handleChange}
        />
      </div>

      <div className="col-6">
        <label className="fs-4">Quantity <span className="text-danger">*</span></label>
        <input
          className="form-control form-control-lg border-2"
          type="number" min="1" name="quantity" placeholder="0"
          value={orderForm.quantity} onChange={handleChange}
        />
      </div>

      {/* ── Customer section ── */}
      <div className="col-12">
        <label className="fs-4">Customer (select existing)</label>
        <Select
          options={customerOptions}
          value={orderForm.customer}
          onChange={(v) => setOrderForm(prev => ({
            ...prev,
            customer:    v || null,
            // Clear manual fields when customer is selected
            ordererName: v ? "" : prev.ordererName,
            phoneNumber: v ? "" : prev.phoneNumber,
          }))}
          placeholder="Search registered customers..."
          isClearable isSearchable
          styles={selectStyles}
        />
      </div>

      {/* Walk-in fields — only shown when no customer selected */}
      {!orderForm.customer && (
        <>
          <div className="col-6">
            <label className="fs-4">Orderer Name</label>
            <input
              className="form-control form-control-lg border-2"
              type="text" name="ordererName"
              placeholder="Walk-in customer name"
              value={orderForm.ordererName} onChange={handleChange}
            />
          </div>
          <div className="col-6">
            <label className="fs-4">Phone Number</label>
            <input
              className="form-control form-control-lg border-2"
              type="text" name="phoneNumber"
              placeholder="Phone number"
              value={orderForm.phoneNumber} onChange={handleChange}
            />
          </div>
        </>
      )}

      {/* Status — only in edit */}
      {isEdit && (
        <div className="col-6">
          <label className="fs-4">Status</label>
          <Select
            options={statusOptions}
            value={editStatus}
            onChange={(v) => setEditStatus(v)}
            styles={selectStyles}
          />
        </div>
      )}

      {/* Description */}
      <div className="col-12">
        <label className="fs-4">Description</label>
        <textarea
          className="form-control border-2" rows={2}
          name="description" placeholder="Optional description..."
          value={orderForm.description} onChange={handleChange}
        />
      </div>

    </div>
  );

  // ── Table columns ──────────────────────────────────────────
  const columns = useMemo(() => {
    const base = [
      { Header: 'No', id: 'no', Cell: ({ row }) => row.index + 1 },
      {
        Header: 'Item', accessor: 'name',
        Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ value, row }) => (
          <div>
            <span className="fw-medium">{value}</span>
            {row.original.isAutoOrder && (
              <span className="badge bg-info rounded-pill px-2 ms-1" style={{ fontSize: "1.25rem" }}>
                Auto
              </span>
            )}
          </div>
        )
      },
      {
        Header: 'Category', accessor: 'category',
        Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ value }) => dash(value)
      },
      { Header: 'Qty', accessor: 'quantity', Cell: ({ value }) => dash(value) },
      {
        Header: 'Customer / Orderer',
        id: 'orderer',
        accessor: (row) =>
          row.customer?.name || row.orderBy?.name || "",
        Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ row }) => {
          const name  = row.original.customer?.name || row.original.orderBy?.name;
          const phone = row.original.customer?.phone || row.original.orderBy?.phoneNumber;
          const isReg = !!row.original.customer;
          return (
            <div>
              <span className="fw-medium">{dash(name)}</span>
              {isReg && (
                <span className="badge bg-success bg-opacity-15 text-success rounded-pill px-2 ms-1"
                  style={{ fontSize: "0.7rem" }}>
                  Registered
                </span>
              )}
              {phone && <p className="text-muted mb-0" style={{ fontSize: "0.78rem" }}>{phone}</p>}
            </div>
          );
        }
      },
      {
        Header: 'Status', accessor: 'status',
        Filter: SelectColumnFilter, filter: 'multiSelectFilter',
        Cell: ({ value }) => (
          <span className={`badge bg-${statusColors[value] || "secondary"} rounded-pill px-3 py-2 fs-6`}>
            {value || "pending"}
          </span>
        )
      },
      {
        Header: 'Note', accessor: 'autoOrderNote',
        Cell: ({ value }) => value
          ? <small className="text-muted">{value}</small>
          : <span className="text-muted">—</span>
      },
      {
        Header: 'Date', accessor: 'createdAt',
        Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—"
      },
    ];

    if (!printView) {
      base.push({
        Header: 'Action',
        Cell: ({ row }) => (
          <>
            <span className="text-warning" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openEditModal(row.original._id)}>
              <MdEditSquare size={24} />
            </span>
            <span className="text-danger" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openDeleteModal(row.original._id)}>
              <MdDeleteForever size={24} />
            </span>
          </>
        )
      });
    }
    return base;
  }, [printView]);

  const data        = useMemo(() => filteredOrders, [filteredOrders]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable({
    columns, data, filterTypes,
    defaultColumn: { Filter: () => null }
  }, useFilters, useGlobalFilter, usePagination);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, setGlobalFilter, pageOptions,
    setPageSize, state, prepareRow,
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  return (
    <div>

      {/* ── ADD MODAL ─────────────────────────────────────────── */}
      <div className="modal fade" ref={addModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">
            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <AiOutlinePlus color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Add Order</h3>
                  <small className="text-muted fs-5">
                    Select registered customer or enter walk-in details.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <form onSubmit={handleAddSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields(false)}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2 fs-2"
                  data-bs-dismiss="modal">Close</button>
                {isLoading ? (
                  <ButtonLoading className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2" disabled>
                    Saving...
                  </ButtonLoading>
                ) : (
                  <button type="submit"
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2">
                    Save Order
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ────────────────────────────────────────── */}
      <div className="modal fade" ref={editModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">
            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <MdEditSquare color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Edit Order</h3>
                  <small className="text-muted fs-5">Update order details and status.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <form onSubmit={handleEditSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields(true)}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2 fs-2"
                  data-bs-dismiss="modal">Cancel</button>
                <button type="submit"
                  className="btn btn-primary flex-fill rounded-3 fw-medium py-2 fs-2">
                  Update Order
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── DELETE MODAL ──────────────────────────────────────── */}
      <div className="modal fade" ref={deleteModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}>
                  <MdDeleteForever color="red" size={34} />
                </div>
                <div>
                  <h5 className="modal-title fw-semibold mb-0">Delete Order</h5>
                  <small className="text-muted">This action cannot be undone.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0">Are you sure you want to delete this order?</p>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0 gap-2">
              <button className="btn btn-primary border flex-fill rounded-3 fw-medium py-2 fs-2"
                data-bs-dismiss="modal">Cancel</button>
              <button className="btn btn-danger flex-fill rounded-3 fw-medium py-2 fs-2"
                onClick={handleDelete}>
                <MdDeleteForever size={18} className="me-1" />Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── HEADER ────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center m-4 px-4 py-2 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <div>
          <h4 className="mb-0 fs-2">Orders</h4>
          <small className="text-muted fs-4">
            {Array.isArray(orders) ? orders.length : 0} total orders
          </small>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <ReactToPrint
            trigger={() => (
              <button className="btn btn-primary rounded-3 fs-2" onClick={handlePrint}>
                Print
              </button>
            )}
            content={() => componentRef.current}
            onAfterPrint={() => setPrintView(false)}
            onBeforeGetContent={handleBeforeGetContent}
          />
          <button className="btn btn-primary rounded-3 fs-2" onClick={openAddModal}>
            <AiOutlinePlus className="me-1" /> Add Order
          </button>
        </div>
      </div>

      {/* ── AUTO-ORDER PANEL ──────────────────────────────────── */}
      {isAdmin && (
        <div className="mx-4 mb-4">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body px-4 py-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div>
                  <h6 className="fw-semibold mb-1 fs-2">🤖 Auto-Order Low Stock</h6>
                  <p className="text-muted mb-0 fs-4">
                    Automatically create orders for products below the stock threshold.
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="fw-medium mb-0 fs-4" style={{ whiteSpace: "nowrap" }}>
                    Threshold:
                  </label>
                  <input
                    type="number" min="1"
                    className="form-control border-2 rounded-3"
                    style={{ width: "80px" }}
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                  />
                  {autoLoading ? (
                    <ButtonLoading className="btn btn-info rounded-3 fs-2" disabled>
                      Running...
                    </ButtonLoading>
                  ) : (
                    <button
                      className="btn btn-info rounded-3 text-white fw-medium fs-2"
                      onClick={handleAutoOrder}>
                      Run Auto-Order
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── STATUS FILTER TABS ────────────────────────────────── */}
      <div className="d-flex gap-2 mx-4 mb-3 flex-wrap">
        {[
          { key: "all",       label: "All" },
          { key: "pending",   label: "Pending" },
          { key: "ordered",   label: "Ordered" },
          { key: "arrived",   label: "Arrived" },
          { key: "cancelled", label: "Cancelled" },
          { key: "auto",      label: "🤖 Auto" },
        ].map(tab => (
          <button key={tab.key}
            className={`btn btn-sm rounded-3 fs-2 ${
              filterStatus === tab.key
                ? `btn-${tab.key === "all" || tab.key === "auto" ? "primary" : statusColors[tab.key] || "secondary"}`
                : "btn-outline-primary"
            }`}
            onClick={() => setFilterStatus(tab.key)}>
            {tab.label}
            {tab.key !== "all" && (
              <span className="ms-1 badge bg-white bg-opacity-25 rounded-pill text-success">
                {tab.key === "auto"
                  ? (Array.isArray(orders) ? orders.filter(o => o.isAutoOrder).length : 0)
                  : (Array.isArray(orders) ? orders.filter(o => o.status === tab.key).length : 0)
                }
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── GLOBAL FILTER ─────────────────────────────────────── */}
      <div className="px-4 mb-4">
        <div className="d-flex align-items-center px-4 rounded-4 border"
          style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>
      </div>

      {/* ── TABLE ─────────────────────────────────────────────── */}
      <div className="px-4 mt-5">
        <div style={{ margin: printView ? "30px" : null }} ref={componentRef}>
          {printView && (
            <>
              <div style={{ padding: "1.5rem" }}><h2>Orders</h2></div>
              <div style={{ marginBottom: '1rem', textAlign: "right", fontSize: "1.5rem" }}>
                Date: {new Date().toLocaleDateString()}
              </div>
            </>
          )}
          <div className="table-responsive rounded-4 border" style={{ backgroundColor: "#ffffff" }}>
            <table className="table table-hover table-striped fs-4"
              {...getTableProps()}
              style={{ minWidth: "100%", fontSize: "12px", margin: 0 }}>
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th className="px-4" style={{ backgroundColor: "#faf8f5" }}
                        {...column.getHeaderProps()}>
                        {column.render('Header')}
                        {!printView && column.canFilter && column.render('Filter')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}
                      style={{
                        opacity: row.original.status === "cancelled" ? 0.5 : 1,
                        backgroundColor: row.original.isAutoOrder ? "#f0f9ff" : undefined,
                      }}>
                      {row.cells.map(cell => (
                        <td className="px-4" style={{ backgroundColor: "transparent" }}
                          {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {page.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="text-center text-muted py-4">
                      No orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: '15px' }}>
            page <strong><em style={{ color: 'blue' }}>{pageIndex + 1}</em> of {pageOptions.length}</strong>
          </span>
          <select className="form-select form-select-lg" style={{ maxWidth: '90px' }}
            value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 20, 30, 50, data.length].map((s, i) => (
              <option key={i} value={s}>Show {s}</option>
            ))}
          </select>
          <li><button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button></li>
          <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={nextPage} disabled={!canNextPage}>Next</button>
          </li>
          <li><button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button></li>
        </ul>
      </div>

    </div>
  );
};